/**
 * @fileoverview Per-document letterhead resolution
 *
 * The renderer asks: "for this document, which letterhead should I draw?"
 * The answer is one of three shapes:
 *   - `{ kind: "none" }`        → blank zones
 *   - `{ kind: "letterhead" }`  → use the named bundle (Beacon, Morgan, …)
 *   - `{ kind: "variant" }`     → use the variant recipe (with its per-zone
 *                                 layered rules)
 *
 * The choice cascades through, in order:
 *   1. Per-document override (set via the picker on the day-one page).
 *   2. The org's firm default letterhead bundle.
 *   3. If there's exactly one bundle, use it (backwards compat for the
 *      single-letterhead case).
 *   4. Nothing.
 *
 * Tag matching (the old auto-resolution from variants) is gone — variants
 * are picked explicitly, not deduced.
 */

import type { Doc, Id } from "@cvx/_generated/dataModel";

/** Resolved variant shape as returned by listVariantsForOrg. */
export type ResolvedLayer = {
  assetId: Id<"letterheadAssets">;
  pageRule: "all" | "firstOnly" | "notFirst" | "oddOnly" | "evenOnly" | "none";
  imageUrl: string | null;
  imageScale: number | null;
  /** Side-zone fit from the asset (null = "fullHeight"). */
  sideFit?: "fullHeight" | "topBlock" | null;
  /** Page-height fraction for "topBlock" assets (null = renderer default). */
  topBlockHeightPct?: number | null;
  /** Source-image crop from the asset (used by "topBlock" side rendering). */
  imageCropTop?: number | null;
  imageCropBottom?: number | null;
  imageCropLeft?: number | null;
  imageCropRight?: number | null;
  /** Header/footer fit from the asset (null = "slot") — see `pageFit.ts`. */
  imageFit?: "slot" | "page" | null;
  /** Where the crop sits on its source page (drives page-fit offsets). */
  sourceSelection?: { top: number; bottom: number } | null;
};

/** Nullable per-edge crop bag as resolved by variantQueries. */
export type ResolvedSideCrop = {
  top?: number | null;
  bottom?: number | null;
  left?: number | null;
  right?: number | null;
} | null;

export type ResolvedVariant = Doc<"letterheadVariants"> & {
  sourceDocumentUrl: string | null;
  headerImageUrl: string | null;
  footerImageUrl: string | null;
  leftSideImageUrl: string | null;
  rightSideImageUrl: string | null;
  resolvedHeaderLayers: ResolvedLayer[] | null;
  resolvedFooterLayers: ResolvedLayer[] | null;
  resolvedLeftSideLayers: ResolvedLayer[] | null;
  resolvedRightSideLayers: ResolvedLayer[] | null;
  /** Resolved side fit for the legacy single-asset path (null = "fullHeight"). */
  leftSideFit?: "fullHeight" | "topBlock" | null;
  rightSideFit?: "fullHeight" | "topBlock" | null;
  leftSideTopBlockHeightPct?: number | null;
  rightSideTopBlockHeightPct?: number | null;
  leftSideCrop?: ResolvedSideCrop;
  rightSideCrop?: ResolvedSideCrop;
};

export type LetterheadOverride =
  | { kind: "none" }
  | { kind: "letterhead"; bundleName: string }
  | { kind: "variant"; variantId: Id<"letterheadVariants"> };

export type ResolvedLetterhead =
  | { kind: "none" }
  | { kind: "letterhead"; bundleName: string }
  | { kind: "variant"; variant: ResolvedVariant };

export function resolveLetterheadForDoc(args: {
  override?: LetterheadOverride | null;
  firmDefaultBundleName?: string | null;
  /** Firm default VARIANT (MOR-953) — wins over the bundle default. */
  firmDefaultVariantId?: Id<"letterheadVariants"> | null;
  variants: ResolvedVariant[];
  bundles: string[];
}): ResolvedLetterhead {
  // 1. Explicit per-doc override wins.
  if (args.override?.kind === "none") return { kind: "none" };
  if (args.override?.kind === "letterhead") {
    return { kind: "letterhead", bundleName: args.override.bundleName };
  }
  if (args.override?.kind === "variant") {
    const variantId = args.override.variantId;
    const v = args.variants.find((x) => x._id === variantId);
    if (v) return { kind: "variant", variant: v };
    // Variant referenced by the override no longer exists — fall through
    // to firm default / single-bundle below instead of rendering nothing
    // unexpectedly.
  }

  // 2. Firm default — variant first (it can express first-page +
  //    subsequent-page layers, which a bundle cannot), then bundle.
  if (args.firmDefaultVariantId) {
    const v = args.variants.find((x) => x._id === args.firmDefaultVariantId);
    if (v) return { kind: "variant", variant: v };
  }
  if (args.firmDefaultBundleName) {
    return { kind: "letterhead", bundleName: args.firmDefaultBundleName };
  }

  // 3. Backwards compat: exactly one bundle → use it.
  if (args.bundles.length === 1) {
    return { kind: "letterhead", bundleName: args.bundles[0] };
  }

  return { kind: "none" };
}
